// Creating a client without leaving the page that needs one. Moved out of
// NewJobPage in round 43 so the new-quote page offers the same button.
import { useState } from 'react';
import { scheduling, type Client } from '../../schedulingApi';
import { ApiError } from '../../api';
import { Banner, Field } from '../../ui';
import { Modal } from '../ui/Modal';

/**
 * Creating a client without leaving the job form.
 *
 * The alternative is what the app did until now: leave, go to Clients, add
 * one, come back, start the job again. Only NAME is required here, matching
 * the server's own schema — ZT asks for four fields, which is ZT's rule and
 * not this one.
 */
export function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (client: Client) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const { client } = await scheduling.createClient({
        name,
        contactPerson,
        phone,
        email,
        address,
      });
      await onCreated(client);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not add that client.');
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="new-client-form" className="btn-primary btn-sm" disabled={busy}>
            {busy ? 'Creating…' : 'Create client'}
          </button>
        </>
      }
    >
      <form id="new-client-form" onSubmit={submit} noValidate className="legacy-page">
        {error && <Banner kind="error">{error}</Banner>}
        <Field label="Name" name="clientName" value={name} onChange={setName} error={fields.name} />
        <div className="row">
          <Field
            label="Contact person"
            name="clientContactPerson"
            value={contactPerson}
            onChange={setContactPerson}
            error={fields.contactPerson}
            required={false}
          />
          <Field
            label="Phone"
            name="clientPhone"
            value={phone}
            onChange={setPhone}
            error={fields.phone}
            required={false}
            inputMode="tel"
          />
        </div>
        <Field
          label="Email"
          name="clientEmail"
          type="email"
          value={email}
          onChange={setEmail}
          error={fields.email}
          required={false}
          autoCapitalize="none"
          spellCheck={false}
          inputMode="email"
        />
        <Field
          label="Address"
          name="clientAddress"
          value={address}
          onChange={setAddress}
          error={fields.address}
          required={false}
        />
      </form>
    </Modal>
  );
}
